import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './CollectionPage.css';

const CollectionPage = () => {
  const { collectionId } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);

  const collections = {
    'black-white': {
      name: 'Black and White Paintings',
      description: 'A study in contrast and form, exploring the depths of monochromatic expression.',
      heroImage: '/bw1.jpg'
    },
    'pink': {
      name: 'Pink Collection',
      description: 'Vibrant pink artworks that capture emotion and energy with bold, expressive brushstrokes.',
      heroImage: '/pink1.jpg'
    },
    'oil': {
      name: 'Oil Collection',
      description: 'Rich oil paintings with deep textures and timeless beauty.',
      heroImage: '/Oil1.jpg'
    },
    'mini': {
      name: 'Mini Collection',
      description: 'Small-scale artworks perfect for intimate spaces, each piece carefully crafted with detail.',
      heroImage: '/mini100.png'
    },
    'lowcountry': {
      name: 'Low Country Summer Collection',
      description: 'Works inspired by the natural rhythms and vibrant colors of Charleston\'s coastal life.',
      heroImage: '/LowCountry1.png'
    }
  };

  const allArtworks = [
    // Low Country Summer Collection
    {
      id: 1,
      title: 'Low Country Summer 1',
      medium: 'Acrylic on Canvas',
      size: '24" x 18"',
      price: 'SOLD',
      image: '/LowCountry1.png',
      collection: 'lowcountry',
      available: false
    },
    {
      id: 2,
      title: 'Low Country Summer 2',
      medium: 'Acrylic on Canvas',
      size: '30" x 30"',
      price: 'SOLD',
      image: '/LowCountry2.jpg',
      collection: 'lowcountry',
      available: false
    },
    {
      id: 3,
      title: 'Low Country Summer 3',
      medium: 'Acrylic on Canvas',
      size: '36" x 24"',
      price: '$600',
      image: '/LowCountry3.png',
      collection: 'lowcountry',
      available: true
    },
    {
      id: 4,
      title: 'Low Country Summer 4',
      medium: 'Acrylic on Canvas',
      size: '36" x 24"',
      price: 'SOLD',
      image: '/LowCountry4.png',
      collection: 'lowcountry',
      available: false
    },
    {
      id: 15,
      title: 'Low Country Summer 5',
      medium: 'Acrylic on Canvas',
      size: '30" x 36"',
      price: '$600',
      image: '/LowCountry5.jpg',
      collection: 'lowcountry',
      available: true
    },
    // Black and White Collection
    {
      id: 5,
      title: 'Black & White Study I',
      medium: 'Acrylic on Canvas',
      size: '24" x 18"',
      price: 'Prints Available Soon',
      image: '/bw1.jpg',
      collection: 'black-white',
      available: false,
      note: 'Original not for sale'
    },
    {
      id: 6,
      title: 'Black & White Study II',
      medium: 'Acrylic on Canvas',
      size: '24" x 18"',
      price: 'Prints Available Soon',
      image: '/bw2.jpg',
      collection: 'black-white',
      available: false,
      note: 'Original not for sale'
    },
    {
      id: 7,
      title: 'Black & White Study III',
      medium: 'Acrylic on Canvas',
      size: '24" x 18"',
      price: 'Prints Available Soon',
      image: '/bw3.jpg',
      collection: 'black-white',
      available: false,
      note: 'Original not for sale'
    },
    // Pink Collection
    {
      id: 8,
      title: 'Pink Dreams I',
      medium: 'Acrylic on Canvas',
      size: '36" x 24"',
      price: 'Prints Available Soon',
      image: '/pink1.jpg',
      collection: 'pink',
      available: false,
      note: 'Original not for sale'
    },
    {
      id: 9,
      title: 'Pink Dreams II',
      medium: 'Acrylic on Canvas',
      size: '36" x 24"',
      price: 'Prints Available Soon',
      image: '/pink2.jpg',
      collection: 'pink',
      available: false,
      note: 'Original not for sale'
    },
    {
      id: 10,
      title: 'Pink Dreams III',
      medium: 'Acrylic on Canvas',
      size: '36" x 24"',
      price: 'Prints Available Soon',
      image: '/pink3.jpg',
      collection: 'pink',
      available: false,
      note: 'Original not for sale'
    },
    // Oil Collection
    {
      id: 11,
      title: 'Oil Study 1',
      medium: 'Oil on Canvas',
      size: '24" x 18"',
      price: '$300',
      image: '/Oil1.jpg',
      collection: 'oil',
      available: true
    },
    {
      id: 12,
      title: 'Oil Study 2',
      medium: 'Oil on Canvas',
      size: '24" x 18"',
      price: '$450',
      image: '/Oil2.jpg',
      collection: 'oil',
      available: true
    },
    // Mini Collection
    {
      id: 13,
      title: 'Mini Artwork 1',
      medium: 'Acrylic on Canvas',
      size: '8" x 12"',
      price: '$100',
      image: '/mini100.png',
      collection: 'mini',
      available: true
    },
    {
      id: 14,
      title: 'Mini Artwork 2',
      medium: 'Acrylic on Canvas',
      size: '8" x 12"',
      price: '$100',
      image: '/mini2.png',
      collection: 'mini',
      available: true
    }
  ];

  const currentCollection = collections[collectionId];
  const artworks = allArtworks.filter(artwork => artwork.collection === collectionId);

  if (!currentCollection) {
    return (
      <div className="collection-page">
        <div className="collection-not-found">
          <h1>Collection Not Found</h1>
          <Link to="/original-work" className="back-button">
            View All Works
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-page">
      <motion.section
        className="collection-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          backgroundImage: `linear-gradient(rgba(7, 55, 99, 0.7), rgba(7, 55, 99, 0.7)), url(${currentCollection.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="collection-hero-content">
          <Link to="/original-work" className="back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to All Works
          </Link>
          <motion.h1
            className="collection-hero-title"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {currentCollection.name}
          </motion.h1>
          <motion.p
            className="collection-hero-subtitle"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {currentCollection.description}
          </motion.p>
        </div>
      </motion.section>

      <section className="collection-gallery-section">
        <div className="collection-gallery-container">
          <motion.div
            className="collection-gallery-grid"
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            {artworks.map((artwork) => (
              <motion.div
                key={artwork.id}
                className="collection-gallery-item"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 }
                }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -10 }}
              >
                <div className="collection-gallery-item-image">
                  <img src={artwork.image} alt={artwork.title} />
                  <div 
                    className="collection-gallery-item-overlay"
                    onClick={() => setSelectedImage(artwork)}
                  >
                    <div className="preview-button">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                      </svg>
                      Preview
                    </div>
                    <div className="collection-gallery-item-info">
                      <h3>{artwork.title}</h3>
                      <p>{artwork.medium}</p>
                      <p>{artwork.size}</p>
                      <span className={`availability ${artwork.available ? 'available' : 'sold'}`}>
                        {artwork.price}
                      </span>
                      {artwork.note && (
                        <p className="artwork-note">{artwork.note}</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="lightbox-close"
                onClick={() => setSelectedImage(null)}
                aria-label="Close"
              >
                ×
              </button>
              <img src={selectedImage.image} alt={selectedImage.title} />
              <div className="lightbox-info">
                <h2>{selectedImage.title}</h2>
                <p>{selectedImage.medium} · {selectedImage.size}</p>
                <span className={`availability ${selectedImage.available ? 'available' : 'sold'}`}>
                  {selectedImage.price}
                </span>
                {selectedImage.note && (
                  <p className="artwork-note">{selectedImage.note}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.section
        className="inquiry-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="inquiry-content">
          <h2>Interested in a Piece?</h2>
          <p>Contact us to inquire about pricing, availability, or commission requests.</p>
          <Link to="/contact" className="inquiry-button">
            Make an Inquiry
          </Link>
        </div>
      </motion.section>
    </div>
  );
};

export default CollectionPage;

